import React, { useState, useEffect } from 'react';
import { listarMotos, listarMovimentacoes, listarFotosMoto, getUrlFoto, fmtMoeda } from '../lib/supabase';
import { Bike, Users, TrendingUp, DollarSign, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function Dashboard({ onNavegar }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const [motos, movs] = await Promise.all([listarMotos(), listarMovimentacoes()]);

      const agora = new Date();
      const inicioMes = new Date(agora.getFullYear(), agora.getMonth(), 1);

      let entMes = 0, saidMes = 0, vendMes = 0;
      const mesesMap = {};

      for (const m of movs) {
        const dt = m.data ? new Date(m.data) : null;
        const desteMs = dt && dt >= inicioMes;
        const mesKey = dt ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}` : null;

        if (!mesesMap[mesKey]) mesesMap[mesKey] = { mes: mesKey, entrada: 0, saida: 0 };
        if (m.tipo === 'Entrada') {
          if (desteMs) { entMes += Number(m.valor) || 0; if (m.categoria === 'Venda de Moto') vendMes++; }
          if (mesKey) mesesMap[mesKey].entrada += Number(m.valor) || 0;
        } else {
          if (desteMs) saidMes += Number(m.valor) || 0;
          if (mesKey) mesesMap[mesKey].saida += Number(m.valor) || 0;
        }
      }

      // Últimas 5 motos
      const ultimas = motos.slice(0, 5);
      const fotosMap = {};
      for (const moto of ultimas) {
        const fotos = await listarFotosMoto(moto.id);
        if (fotos.length > 0) {
          fotosMap[moto.id] = await getUrlFoto(fotos[0].caminho);
        }
      }

      // Dados para gráfico (últimos 6 meses)
      const mesesOrdenados = Object.values(mesesMap)
        .filter(m => m.mes)
        .sort((a, b) => a.mes.localeCompare(b.mes))
        .slice(-6)
        .map(m => ({
          ...m,
          mesLabel: new Date(m.mes + '-01').toLocaleString('pt-BR', { month: 'short', year: '2-digit' }),
        }));

      // Status das motos para pizza
      const statusCount = motos.reduce((acc, m) => {
        const s = m.status || 'Disponível';
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {});

      setDados({
        totalMotos: motos.length,
        motosDisponiveis: motos.filter(m => m.status === 'Disponível').length,
        motosConsignadas: motos.filter(m => m.status === 'Consignada').length,
        vendasMes: vendMes,
        faturamentoMes: entMes,
        lucroMes: entMes - saidMes,
        ultimas,
        fotosMap,
        graficoMeses: mesesOrdenados,
        pizza: Object.entries(statusCount).map(([nome, valor]) => ({ nome, valor })),
      });
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dados');
    } finally {
      setCarregando(false);
    }
  }

  if (carregando) return <div className="loading"><span>Carregando dashboard...</span></div>;
  if (erro) return (
    <div className="alert alert-error" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <strong>Erro ao conectar com Supabase</strong><br />
        <small>{erro}</small><br />
        <small>Verifique as variáveis de ambiente REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY</small>
      </div>
    </div>
  );

  const CORES_PIZZA = ['#22C55E', '#EF4444', '#F97316', '#3B82F6', '#8B5CF6'];

  const hoje = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const hojeFormatado = hoje.charAt(0).toUpperCase() + hoje.slice(1);

  return (
    <div className="dash">
      {/* ── Hero ── */}
      <div className="dash-hero">
        <div>
          <h2 className="dash-hello">Olá, bem-vindo! 👋</h2>
          <p className="dash-date">{hojeFormatado}</p>
        </div>
        <button className="btn btn-amarelo" onClick={() => onNavegar?.('estoque')}>
          <Bike size={16} />
          Nova Moto
        </button>
      </div>

      {/* ── KPIs ── */}
      <div className="kpi-grid">
        <KpiCard icon={<Bike size={22} />} label="Total de Motos" value={dados.totalMotos} sub={`${dados.motosDisponiveis} disponíveis`} cor="azul" />
        <KpiCard icon={<TrendingUp size={22} />} label="Vendas no Mês" value={dados.vendasMes} sub="unidades vendidas" cor="verde" />
        <KpiCard icon={<DollarSign size={22} />} label="Faturamento" value={fmtMoeda(dados.faturamentoMes)} sub="entradas no mês" cor="amarelo" />
        <KpiCard icon={<Users size={22} />} label="Lucro do Mês" value={fmtMoeda(dados.lucroMes)} sub={dados.lucroMes >= 0 ? 'positivo' : 'negativo'} cor={dados.lucroMes >= 0 ? 'verde' : 'vermelho'} />
      </div>

      {/* ── Gráficos ── */}
      <div className="dash-charts">
        {dados.graficoMeses.length > 0 && (
          <div className="card chart-card">
            <h3 className="chart-title">Entradas & Saídas — últimos meses</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dados.graficoMeses}>
                <defs>
                  <linearGradient id="gradEnt" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradSaid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F5" />
                <XAxis dataKey="mesLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} />
                <Tooltip formatter={(v) => fmtMoeda(v)} />
                <Area type="monotone" dataKey="entrada" stroke="#22C55E" fill="url(#gradEnt)" name="Entradas" strokeWidth={2} />
                <Area type="monotone" dataKey="saida" stroke="#EF4444" fill="url(#gradSaid)" name="Saídas" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {dados.pizza.length > 0 && (
          <div className="card pizza-card">
            <h3 className="chart-title">Status do Estoque</h3>
            <div className="pizza-wrap">
              <PieChart width={160} height={160}>
                <Pie data={dados.pizza} cx={75} cy={75} innerRadius={45} outerRadius={72} dataKey="valor" paddingAngle={3}>
                  {dados.pizza.map((_, i) => <Cell key={i} fill={CORES_PIZZA[i % CORES_PIZZA.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n, p) => [v, p.payload.nome]} />
              </PieChart>
              <div className="pizza-legend">
                {dados.pizza.map((item, i) => (
                  <div key={i} className="pizza-item">
                    <span className="pizza-dot" style={{ background: CORES_PIZZA[i % CORES_PIZZA.length] }} />
                    <span className="pizza-label">{item.nome}</span>
                    <strong>{item.valor}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Últimas Motos ── */}
      {dados.ultimas.length > 0 && (
        <div className="card">
          <div className="section-header">
            <h3 className="chart-title">Últimas Motos Cadastradas</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => onNavegar?.('estoque')}>Ver todas</button>
          </div>
          <div className="motos-list">
            {dados.ultimas.map((moto) => (
              <MotoCard key={moto.id} moto={moto} fotoUrl={dados.fotosMap[moto.id]} />
            ))}
          </div>
        </div>
      )}

      <style>{`
        .dash { display: flex; flex-direction: column; gap: 20px; }
        .dash-hero { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
        .dash-hello { font-size: 22px; font-weight: 700; color: var(--azul); }
        .dash-date { font-size: 13px; color: var(--cinza-t); margin-top: 2px; }
        .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
        .dash-charts { display: grid; grid-template-columns: 1fr 280px; gap: 14px; }
        @media (max-width: 900px) { .dash-charts { grid-template-columns: 1fr; } }
        .chart-card { flex: 1; }
        .pizza-card {}
        .chart-title { font-size: 14px; font-weight: 700; color: var(--azul); margin-bottom: 14px; }
        .pizza-wrap { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .pizza-legend { display: flex; flex-direction: column; gap: 8px; flex: 1; }
        .pizza-item { display: flex; align-items: center; gap: 8px; font-size: 13px; }
        .pizza-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .pizza-label { flex: 1; color: var(--cinza-t); }
        .section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .motos-list { display: flex; flex-direction: column; gap: 10px; }
      `}</style>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, cor }) {
  const cores = {
    azul: { bg: '#EFF3FF', fg: '#0B2341', ic: '#3B82F6' },
    verde: { bg: '#DCFCE7', fg: '#15803D', ic: '#22C55E' },
    amarelo: { bg: '#FEF9C3', fg: '#92400E', ic: '#F59E0B' },
    vermelho: { bg: '#FEE2E2', fg: '#DC2626', ic: '#EF4444' },
  };
  const c = cores[cor] || cores.azul;
  return (
    <div className="card kpi-card">
      <div className="kpi-icon" style={{ background: c.bg, color: c.ic }}>{icon}</div>
      <div className="kpi-body">
        <p className="kpi-label">{label}</p>
        <p className="kpi-value" style={{ color: c.fg }}>{value}</p>
        <p className="kpi-sub">{sub}</p>
      </div>
      <style>{`
        .kpi-card { display: flex; align-items: flex-start; gap: 14px; }
        .kpi-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .kpi-body { flex: 1; }
        .kpi-label { font-size: 12px; color: var(--cinza-t); font-weight: 500; text-transform: uppercase; letter-spacing: .4px; }
        .kpi-value { font-size: 22px; font-weight: 800; line-height: 1.2; margin: 2px 0; }
        .kpi-sub { font-size: 11px; color: var(--cinza-t); }
      `}</style>
    </div>
  );
}

function MotoCard({ moto, fotoUrl }) {
  const corStatus = { 'Disponível': 'verde', 'Vendida': 'vermelho', 'Reservada': 'laranja', 'Consignada': 'azul' };
  const cor = corStatus[moto.status] || 'cinza';
  return (
    <div className="moto-row">
      <div className="moto-row-foto">
        {fotoUrl ? <img src={fotoUrl} alt="moto" /> : <span>📷</span>}
      </div>
      <div className="moto-row-info">
        <strong>{moto.marca} {moto.modelo}</strong>
        <span>{moto.ano} · {moto.placa || 'Sem placa'}</span>
      </div>
      <div className="moto-row-right">
        <span className={`badge badge-${cor}`}>{moto.status || 'Disponível'}</span>
        {moto.valor_venda && <span className="moto-valor">{fmtMoeda(parseFloat(moto.valor_venda) || 0)}</span>}
      </div>
      <style>{`
        .moto-row { display: flex; align-items: center; gap: 14px; padding: 10px; border-radius: 10px; background: var(--cinza-l); }
        .moto-row-foto { width: 52px; height: 42px; border-radius: 8px; overflow: hidden; background: var(--cinza-m); display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
        .moto-row-foto img { width: 100%; height: 100%; object-fit: cover; }
        .moto-row-info { flex: 1; min-width: 0; }
        .moto-row-info strong { font-size: 13px; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .moto-row-info span { font-size: 11px; color: var(--cinza-t); }
        .moto-row-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
        .moto-valor { font-size: 12px; font-weight: 700; color: var(--azul); }
      `}</style>
    </div>
  );
}
